import React, { useState, useMemo, useEffect } from 'react';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { confirmSignOutWithUnsent } from '../config/authRoutes';
import {
  LayoutDashboard, TrendingUp, FolderOpen, Store, DollarSign,
  ShieldAlert, Shield, Users, Megaphone, Handshake, PenLine, FileText,
  Tag, Layers, Home, LogOut, BookOpen, Wallet,
  Building2, Settings, FlaskConical, ShieldCheck,
  QrCode, CalendarDays, HeartPulse, Stethoscope, Receipt, CheckSquare, Radar, Contact, MapPin, ServerCog, KeyRound, Briefcase,
  MessageSquare,
  Compass,
  ClipboardCheck,
  Activity,
  Workflow,
  type LucideIcon,
} from 'lucide-react';
import { TENANT } from '../config';
import { apiRequest } from '../services/api';
import { rememberWorkspace } from '../services/workspacesApi';
import { WorkspaceLayout, SidebarTrigger, type SidebarGroup, type SidebarItem } from '../components/workspaceSidebar';
import { Button } from '../components/shadcn/ui/button';
import { Badge } from '../components/shadcn/ui/badge';
import { ViewTransition } from '../components/shadcn/motion';

/* ── Sandbox trial types ──────────────────────────────────────── */
interface SandboxUsageMeter { used: number; limit: number }
interface SandboxStatus {
  isSandbox: boolean;
  daysLeft?: number;
  expiresAt?: string;
  usage?: {
    esigns: SandboxUsageMeter;
    offerings: SandboxUsageMeter;
    properties: SandboxUsageMeter;
  };
}

/* ── Sandbox usage meter bar ───────────────────────────────────── */
function UsageMeter({ label, used, limit }: { label: string; used: number; limit: number }) {
  const pct = Math.min(100, Math.round((used / limit) * 100));
  const color = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-400' : 'bg-emerald-500';
  return (
    <div className="flex flex-col gap-0.5 min-w-[80px]">
      <div className="flex justify-between text-[10px] text-amber-800 dark:text-amber-300 font-medium">
        <span>{label}</span>
        <span>{used}/{limit}</span>
      </div>
      <div className="h-1.5 rounded-full bg-amber-200 dark:bg-amber-800">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/* ── Sandbox trial banner (shown only in sandbox mode) ─────────── */
function SandboxBanner() {
  const [status, setStatus] = useState<SandboxStatus | null>(null);

  useEffect(() => {
    // Only fetch in sandbox builds — REACT_APP_PAYMENTS_ENV is baked in at build time.
    if (process.env.REACT_APP_PAYMENTS_ENV !== 'sandbox') return;
    apiRequest<SandboxStatus>('GET', '/sandbox/status')
      .then(r => { if (r.success && r.data) setStatus(r.data); })
      .catch(() => {/* not in sandbox or no trial — hide silently */});
  }, []);

  if (!status?.isSandbox || !status.usage) return null;

  return (
    <div className="bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-800 px-3 sm:px-4 py-2 flex flex-wrap items-center gap-2 sm:gap-4">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-900 dark:text-amber-200 shrink-0">
        <FlaskConical className="w-3.5 h-3.5" />
        Sandbox Trial — {status.daysLeft ?? 0} day{status.daysLeft !== 1 ? 's' : ''} remaining
      </div>
      <div className="flex flex-wrap items-center gap-3 sm:gap-4 flex-1 min-w-0">
        <UsageMeter label="E-Signs" used={status.usage.esigns.used} limit={status.usage.esigns.limit} />
        <UsageMeter label="Offerings" used={status.usage.offerings.used} limit={status.usage.offerings.limit} />
        <UsageMeter label="Properties" used={status.usage.properties.used} limit={status.usage.properties.limit} />
      </div>
      <span className="text-[10px] text-amber-700 dark:text-amber-400 shrink-0">
        All data is test-mode only
      </span>
    </div>
  );
}

/* ── Greeting ─────────────────────────────────────────────────── */
function useGreeting(firstName: string) {
  return useMemo(() => {
    const h = new Date().getHours();
    const period = h < 12 ? 'morning' : h < 17 ? 'afternoon' : 'evening';
    return `Good ${period}, ${firstName}`;
  }, [firstName]);
}

/* ── Nav structure ────────────────────────────────────────────── */
interface NavItem { label: string; path: string; icon: LucideIcon }
interface NavGroup { category: string; items: NavItem[] }

function useNavGroups(): NavGroup[] {
  const { sandboxTenantId } = useUser();
  // When impersonating/logged in as a sandbox tenant admin, suppress platform-only items
  const isSandboxTenant = Boolean(sandboxTenantId);

  return useMemo(() => {
    const groups: NavGroup[] = [
      {
        category: 'Ops',
        items: [
          ...(TENANT.id !== 'emerge' ? [
            { label: 'Capital Raises', path: '/admin/capital-raises',   icon: TrendingUp },
            { label: 'Projects',       path: '/admin/projects',         icon: FolderOpen },
          ] : []),
          ...(TENANT.id === 'emerge' ? [
            { label: 'Operational Command', path: '/admin/command', icon: Radar },
          ] : []),
          { label: 'Calendar',       path: '/admin/calendar',         icon: CalendarDays },
          // The sales reps' workspace works off the calendar above it (2026-09-30).
          ...(TENANT.id === 'emerge' ? [
            { label: 'Business Development', path: '/admin/business-development', icon: Briefcase },
          ] : []),
          ...(!isSandboxTenant ? [
            { label: 'Vendors',        path: '/admin/vendors',          icon: Store },
            { label: 'Payroll',        path: '/admin/payroll',          icon: DollarSign },
            { label: 'Risk',           path: '/admin/risk',             icon: ShieldAlert },
            { label: 'AML Compliance', path: '/admin/aml',              icon: Shield },
          ] : []),
          ...(TENANT.id === 'redex' && !isSandboxTenant ? [{ label: 'Capital Desk', path: '/admin/capital-desk', icon: Building2 }] : []),
        ],
      },
      {
        category: 'Marketing',
        items: [
          { label: 'Contacts',  path: '/admin/contacts',  icon: Users },
          ...(!isSandboxTenant ? [
            { label: 'Campaigns', path: '/admin/campaigns', icon: Megaphone },
            { label: 'Business Cards', path: '/admin/business-cards', icon: Contact },
            { label: 'Event Funnels', path: '/admin/event-funnels', icon: QrCode },
          ] : []),
        ],
      },
      {
        category: 'Finance',
        items: [
          { label: 'Engagements', path: '/admin/engagements', icon: Handshake },
          ...(!isSandboxTenant ? [
            { label: 'Transactions', path: '/admin/transactions', icon: DollarSign },
          ] : []),
          // E-Signatures is the deal-flow signing screen: it lists envelopes in
          // capital-markets terms and its send form takes a single document.
          // eMerge has its own under eMerge Operations > Signatures, which
          // sends packets and designates a counter-signer. Two screens doing
          // the same job differently is how people end up using the wrong one.
          ...(TENANT.id !== 'emerge' ? [
            { label: 'E-Signatures', path: '/admin/esignatures', icon: PenLine },
          ] : []),
          // Counter-Sign is a HiveFund page. It reads subscription_agreements,
          // counter-signs page 18 of a subscription document, and displays
          // "Honeyshares Management LLC" and a named fund manager. On eMerge it
          // was in the nav, reachable, and showed another company's name.
          //
          // eMerge counter-signs through the ordinary signing link: a second
          // recipient on the envelope at signing order 2. There is nothing for
          // this page to do here.
          ...(TENANT.id !== 'emerge' ? [
            { label: 'Counter-Sign', path: '/admin/countersign', icon: ShieldCheck },
          ] : []),
        ],
      },
      {
        category: 'Content',
        items: [
          { label: 'Offerings',                   path: '/admin/offerings',  icon: Tag },
          { label: TENANT.branding.propertyLabel, path: '/admin/properties', icon: Building2 },
          { label: 'Documents',                   path: '/admin/documents',  icon: FileText },
          ...(!isSandboxTenant ? [
            { label: 'Page Builder', path: '/admin/page-builder', icon: Layers },
          ] : []),
        ],
      },
      ...(TENANT.id === 'emerge' ? [{
        category: 'eMerge Operations',
        items: [
          // The workforce workspaces themselves. Each still checks its own
          // capability; an administrator without the role sees its refusal.
          { label: 'Patient Advocate', path: '/portal/patient-advocate', icon: HeartPulse },
          { label: 'UMED',             path: '/portal/umed',             icon: ShieldCheck },
          { label: 'Provider',         path: '/provider',                icon: Stethoscope },
          { label: 'Access Review',    path: '/admin/emerge/access-review',           icon: KeyRound },
          { label: 'Signatures',       path: '/admin/emerge/signatures',              icon: PenLine },
          { label: 'Facility Map',     path: '/admin/emerge-map',                     icon: MapPin },
          { label: 'Coverage',         path: '/admin/emerge/coverage',                icon: Compass },
          { label: 'Facilities',       path: '/admin/emerge/facilities',              icon: Building2 },
          { label: 'Patients',         path: '/admin/emerge/patients',                icon: Users },
          { label: 'Scheduling',       path: '/admin/emerge/scheduling',              icon: CalendarDays },
          { label: 'Completed Visits', path: '/admin/emerge/completed_visits',        icon: CheckSquare },
          { label: 'Hearing Aids',     path: '/admin/emerge/hearing_aids',            icon: Stethoscope },
          { label: 'Qualification',    path: '/admin/emerge/patient_qualification',   icon: ShieldCheck },
          { label: 'Advocate Leads',   path: '/admin/emerge/patient_advocate_leads',  icon: HeartPulse },
          { label: 'Finance Accounts', path: '/admin/emerge/finance_accounts',        icon: DollarSign },
          { label: 'Invoices',         path: '/admin/emerge/invoices',                icon: Receipt },
          { label: 'Payments',         path: '/admin/emerge/payments',                icon: Wallet },
          { label: 'Feedback',         path: '/admin/emerge/feedback',                icon: MessageSquare },
          { label: 'Workflow Health',  path: '/admin/emerge/workflow-health',         icon: Activity },
          { label: 'Record Flow',      path: '/admin/record-flow',                    icon: Workflow },
          { label: 'Account Recovery', path: '/admin/emerge/account-recovery',        icon: KeyRound },
          // The workforce (employees and admins), with read-only Sign in as (2026-10-26).
          { label: 'Users',            path: '/admin/users',                          icon: Users },
        ],
      }] : []),
      // Sandbox wizard — only rendered when REACT_APP_PAYMENTS_ENV=sandbox is baked
      // into the build. Production builds never set this variable so this block is
      // compiled away and the nav item never appears in prod.
      ...(process.env.REACT_APP_PAYMENTS_ENV === 'sandbox' ? [{
        category: 'Sandbox',
        items: [
          { label: 'Trial Setup', path: '/admin/sandbox/setup',    icon: FlaskConical },
          { label: 'Tenants',     path: '/admin/sandbox/tenants',  icon: Users },
        ],
      }] : []),
    ];
    return groups;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSandboxTenant]);
}

/* ── Sidebar ──────────────────────────────────────────────────── */
// The console's frame is the one standard sidebar (components/workspaceSidebar): every item a real link, the active one
// marked aria-current. Logout is an action, not a link, so it lives in the header.
const toItem = (item: NavItem, active: boolean): SidebarItem => ({ key: item.path, label: item.label, icon: item.icon, href: item.path, active });

/* ── Main layout ──────────────────────────────────────────────── */
const AdminLayout: React.FC = () => {
  const { user, logout, sandboxTenantId } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const [sandboxPayments, setSandboxPayments] = useState(() =>
    localStorage.getItem('sandboxPayments') === 'true'
  );

  const isSandboxTenant = Boolean(sandboxTenantId);
  const firstName = user?.email?.split('@')[0] || 'Admin';
  const greeting = useGreeting(firstName);
  const groups = useNavGroups();

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  // EP-005: the console is remembered as the last workspace used, so an
  // administrator who works here lands here; one who last worked in a
  // workforce workspace lands there instead.
  useEffect(() => {
    if (TENANT.id === 'emerge') rememberWorkspace('ADMIN');
  }, []);

  const handleLogout = async () => {
    if (!confirmSignOutWithUnsent(user?.id)) return; // unsent notes on this computer: ask first
    await logout();
    navigate('/');
  };

  const toggleSandboxPayments = () => {
    const next = !sandboxPayments;
    setSandboxPayments(next);
    localStorage.setItem('sandboxPayments', String(next));
  };

  const sidebarGroups: SidebarGroup[] = [
    {
      key: 'dashboard', label: 'Overview',
      items: [toItem({ label: 'Dashboard', path: '/admin/dashboard', icon: LayoutDashboard }, isActive('/admin/dashboard'))],
    },
    ...groups.map((g) => ({ key: g.category, label: g.category, items: g.items.map((it) => toItem(it, isActive(it.path))) })),
  ];

  // The doors out of the console, each under the tenant condition it has always had.
  const door = (label: string, href: string, icon: LucideIcon): SidebarItem => ({ key: href, label, icon, href, active: isActive(href) });
  const doors: SidebarItem[] = [
    ...(!isSandboxTenant ? [door('Treasury', '/admin/wallet', Wallet)] : []),
    // The provider portal, for admins who also hold a ratified
    // PRACTITIONER assignment. It lives outside /admin because it is
    // the providers' own surface; this is a door to it, not a copy.
    ...(TENANT.id === 'emerge' ? [door('Provider Portal', '/provider', Stethoscope)] : []),
    // The advocate workspace (EP-004C). Same reasoning: the advocates'
    // own surface, read-only, gated server-side by ADVOCACY_LEAD_VIEW.
    ...(TENANT.id === 'emerge' ? [door('Advocate Portal', '/portal/patient-advocate', HeartPulse)] : []),
    // The UMED operations workspace (EP-004U phase 2), directly under the
    // advocate portal because that is the order the work happens in: an
    // advocate enrols the resident, then a UMED is created, submitted and
    // chased through DCF approval.
    //
    // Read-only, and gated server-side by UMED_CASE_VIEW plus the eMerge
    // PHI operator list. Like the two doors above it, this is a link and
    // not a permission -- an admin without a ratified UMED role sees the
    // link and is refused by the server, which tells them which of the
    // two gates they are missing.
    ...(TENANT.id === 'emerge' ? [door('UMED Operations', '/portal/umed', ClipboardCheck)] : []),
    door('System Admin', '/admin/system', ServerCog),
    // eMerge: Users is a normal item under eMerge Operations (2026-10-26); this footer copy is the investor
    // tenants' entry to their investor-user admin.
    ...(TENANT.id !== 'emerge' ? [door('Users', '/admin/users', Settings)] : []),
    door('Access Requests', '/admin/access-requests', ShieldAlert),
    // Investor surface, and it links to /portal/invest/properties. eMerge
    // has no investment product, so the destination is meaningless there.
    ...(TENANT.id !== 'emerge' ? [door('Investor Portal', '/portal/invest/properties', Home)] : []),
  ];
  const footerGroups: SidebarGroup[] = [{ key: 'doors', label: 'More', items: doors }];

  return (
    <div className="min-h-screen bg-background portal-typography overflow-x-hidden">
      <WorkspaceLayout className="min-h-screen" workspace="ADMIN" navLabel="Admin console" groups={sidebarGroups} footer={footerGroups}>
        {/* Top header */}
        <header className="sticky top-0 z-40 bg-[#F0F0F0] border-b border-border flex items-center justify-between gap-2 px-3 sm:px-4 h-14 sm:h-16 shrink-0">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <SidebarTrigger />
            <h1 className="text-sm font-medium text-foreground truncate">{greeting}</h1>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <Badge variant="secondary" className="hidden sm:inline-flex rounded-full">
              Admin
            </Badge>
            {/* Sandbox payments toggle — admin-only, runtime switch */}
            {user?.role === 'admin' && (
              <button
                onClick={toggleSandboxPayments}
                className={`text-xs px-2 sm:px-2.5 py-1 rounded-full font-semibold flex items-center gap-1 transition-colors ${
                  sandboxPayments
                    ? 'bg-amber-100 text-amber-800 ring-1 ring-amber-300'
                    : 'bg-muted text-foreground hover:bg-muted/80'
                }`}
                title={sandboxPayments ? 'Sandbox payments ON — Plaid/Increase use test credentials' : 'Click to enable sandbox payment mode'}
              >
                <FlaskConical className="w-3 h-3" />
                <span className="hidden sm:inline">{sandboxPayments ? 'Sandbox ON' : 'Sandbox'}</span>
              </button>
            )}
            {process.env.REACT_APP_PAYMENTS_ENV === 'sandbox' && (
              <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-800 font-semibold flex items-center gap-1">
                <FlaskConical className="w-3 h-3" /> Sandbox
              </span>
            )}
            <button
              onClick={() => navigate('/admin/documentation')}
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-black/5 transition-colors"
              aria-label="Documentation"
            >
              <BookOpen className="w-4 h-4" />
            </button>
            <Button type="button" variant="ghost" size="sm" className="text-[var(--tide-status-danger)] hover:bg-[var(--tide-status-danger-bg)] hover:text-[var(--tide-status-danger)]" onClick={handleLogout}>
              <LogOut aria-hidden="true" /> Logout
            </Button>
          </div>
        </header>

        {/* Sandbox trial banner — visible only when PAYMENTS_ENV=sandbox */}
        <SandboxBanner />

        {/* Sandbox payments mode banner */}
        {sandboxPayments && (
          <div className="bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-800 px-3 sm:px-4 py-1.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 sm:gap-3">
            <div className="flex items-start sm:items-center gap-2 text-xs font-medium text-amber-800 dark:text-amber-200 min-w-0">
              <FlaskConical className="w-3.5 h-3.5 shrink-0 mt-0.5 sm:mt-0" />
              Sandbox Payment Mode — Plaid &amp; Increase using test credentials. Transactions are not real.
            </div>
            <button
              onClick={toggleSandboxPayments}
              className="text-left sm:text-right text-xs text-amber-700 dark:text-amber-300 hover:text-amber-900 font-semibold underline shrink-0"
            >
              Disable
            </button>
          </div>
        )}

        {/* Page content */}
        <main className="admin-content flex-1 min-w-0 w-full max-w-7xl mx-auto px-3 py-4 sm:px-5 sm:py-5 lg:px-6 lg:py-6 overflow-x-hidden">
          <ViewTransition viewKey={location.pathname}>
            <Outlet />
          </ViewTransition>
        </main>
      </WorkspaceLayout>
    </div>
  );
};

export default AdminLayout;
